import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';

const Breadcrumb = ({ 
  items = [], 
  separator = 'ChevronRight',
  showHome = true,
  homeLabel = 'Home',
  className,
  itemClassName,
  separatorClassName,
  currentClassName,
  linkClassName,
  ...props 
}) => {
  const location = useLocation();

  // Auto-generate breadcrumbs if not provided
  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs(location.pathname);

  // Add home breadcrumb if requested and not already present
  const finalItems = showHome && breadcrumbItems.length > 0 && breadcrumbItems[0].href !== '/' 
    ? [{ name: homeLabel, href: '/' }, ...breadcrumbItems]
    : breadcrumbItems;

  if (finalItems.length <= 1 && !showHome) {
    return null; // Don't show breadcrumbs for home page or single-level pages unless forced
  }

  return (
    <nav 
      aria-label="Breadcrumb navigation"
      className={cn(
        "flex items-center space-x-1 text-sm text-muted-foreground",
        className
      )}
      {...props}
    >
      <ol className="flex items-center space-x-1" role="list">
        {finalItems.map((item, index) => {
          const isLast = index === finalItems.length - 1;
          
          return (
            <li key={index} className="flex items-center space-x-1">
              {index > 0 && (
                <Icon 
                  name={separator} 
                  size={14} 
                  className={cn(
                    "text-muted-foreground",
                    separatorClassName
                  )}
                  aria-hidden="true"
                />
              )}
              
              {isLast ? (
                <span 
                  className={cn(
                    "font-medium text-foreground",
                    itemClassName,
                    currentClassName
                  )}
                  aria-current="page"
                >
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    "hover:text-primary transition-colors duration-200",
                    "focus:outline-none focus:text-primary focus:underline",
                    itemClassName,
                    linkClassName
                  )}
                  aria-label={`Navigate to ${item.name}`}
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

// Auto-generate breadcrumbs based on URL path
const generateBreadcrumbs = (pathname) => {
  // Define the site structure and mapping
  const siteStructure = {
    '/': { name: 'Home', href: '/' },
    '/gcc-setup-services-comprehensive-mastery': { 
      name: 'GCC Setup Services', 
      href: '/gcc-setup-services-comprehensive-mastery',
      parent: '/'
    },
    '/market-intelligence-hub-real-time-insights': { 
      name: 'Market Intelligence', 
      href: '/market-intelligence-hub-real-time-insights',
      parent: '/'
    },
    '/success-stories-proven-transformations': { 
      name: 'Success Stories', 
      href: '/success-stories-proven-transformations',
      parent: '/'
    },
    '/roi-calculator-hub-interactive-decision-tools': { 
      name: 'ROI Calculator', 
      href: '/roi-calculator-hub-interactive-decision-tools',
      parent: '/'
    },
    '/resources-insights-knowledge-authority': { 
      name: 'Resources & Insights', 
      href: '/resources-insights-knowledge-authority',
      parent: '/'
    },
    '/engagement-models-partnership-framework': { 
      name: 'Partnership Framework', 
      href: '/engagement-models-partnership-framework',
      parent: '/'
    },
    '/about-us-company-foundation-leadership': { 
      name: 'About Us', 
      href: '/about-us-company-foundation-leadership',
      parent: '/'
    },
    '/contact': { 
      name: 'Executive Contact', 
      href: '/contact',
      parent: '/'
    },
    // Add special cases for webinar player
    '/webinar': { 
      name: 'Webinars', 
      href: '/resources-insights-knowledge-authority',
      parent: '/resources-insights-knowledge-authority'
    },
    // Admin routes
    '/admin': { 
      name: 'Admin Panel', 
      href: '/admin',
      parent: '/'
    },
    '/admin-access': { 
      name: 'Admin Access', 
      href: '/admin-access',
      parent: '/'
    }
  };

  const currentPage = siteStructure[pathname];
  
  if (!currentPage) {
    // Handle dynamic routes like /webinar/123
    if (pathname.startsWith('/webinar/')) {
      return [
        { name: 'Resources & Insights', href: '/resources-insights-knowledge-authority' },
        { name: 'Webinar', href: pathname }
      ];
    }
    
    // Handle unknown routes - create breadcrumb from path segments
    const segments = pathname.split('/').filter(Boolean);
    return segments.map((segment, index) => {
      const path = '/' + segments.slice(0, index + 1).join('/');
      const name = segment.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      return { name, href: path };
    });
  }

  // Build breadcrumb trail
  const breadcrumbs = [];
  let current = currentPage;
  
  while (current && current.href !== '/') {
    breadcrumbs.unshift({ name: current.name, href: current.href });
    current = current.parent ? siteStructure[current.parent] : null;
  }

  return breadcrumbs;
};

// Enhanced breadcrumb with additional features
export const EnhancedBreadcrumb = ({ 
  items = [],
  showSchema = true,
  className,
  ...props 
}) => {
  const location = useLocation();
  
  // Generate structured data for SEO
  const generateStructuredData = (breadcrumbItems) => {
    if (!showSchema || breadcrumbItems.length <= 1) return null;

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${window.location.origin}${item.href}`
      }))
    };

    return (
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    );
  };

  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs(location.pathname);
  const finalItems = breadcrumbItems.length > 0 && breadcrumbItems[0].href !== '/' 
    ? [{ name: 'Home', href: '/' }, ...breadcrumbItems]
    : breadcrumbItems;

  return (
    <>
      {generateStructuredData(finalItems)}
      <Breadcrumb 
        items={finalItems}
        showHome={false} // We manually add home above
        className={cn(
          "py-3 px-4 bg-muted/30 rounded-lg border border-border/50",
          className
        )}
        {...props}
      />
    </>
  );
};

// Compact breadcrumb for mobile or tight spaces
export const CompactBreadcrumb = ({ items = [], className, ...props }) => {
  const location = useLocation();
  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs(location.pathname);
  
  if (breadcrumbItems.length <= 1) return null;

  const finalItems = breadcrumbItems.length > 0 && breadcrumbItems[0].href !== '/' 
    ? [{ name: 'Home', href: '/' }, ...breadcrumbItems]
    : breadcrumbItems;

  // Show only last 2 items on mobile
  const compactItems = finalItems.length > 2 
    ? [
        finalItems[0], // Home
        { name: '...', href: '#', disabled: true },
        ...finalItems.slice(-2) // Last 2 items
      ]
    : finalItems;

  return (
    <Breadcrumb
      items={compactItems}
      showHome={false}
      className={cn("text-xs", className)}
      separator="ChevronRight"
      {...props}
    />
  );
};

// Page header with integrated breadcrumb
export const PageHeaderWithBreadcrumb = ({
  title,
  description,
  breadcrumbItems = [],
  actions,
  className,
  ...props
}) => {
  return (
    <div className={cn("space-y-4", className)} {...props}>
      <EnhancedBreadcrumb items={breadcrumbItems} />
      
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <h1 className="text-heading-xl font-bold text-foreground">
            {title}
          </h1>
          {description && (
            <p className="text-lead text-muted-foreground max-w-3xl">
              {description}
            </p>
          )}
        </div>
        
        {actions && (
          <div className="flex items-center space-x-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

// Breadcrumb with icons for categories
export const IconBreadcrumb = ({ items = [], iconMap = {}, className, ...props }) => {
  const location = useLocation();
  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs(location.pathname);
  
  const defaultIconMap = {
    'Home': 'Home',
    'GCC Setup Services': 'Building',
    'Market Intelligence': 'TrendingUp',
    'Success Stories': 'Award',
    'ROI Calculator': 'Calculator',
    'Resources & Insights': 'BookOpen',
    'Partnership Framework': 'Handshake',
    'About Us': 'Users',
    'Executive Contact': 'Phone',
    'Admin Panel': 'Settings',
    'Webinar': 'Play'
  };

  const finalIconMap = { ...defaultIconMap, ...iconMap };

  return (
    <nav 
      aria-label="Breadcrumb navigation"
      className={cn(
        "flex items-center space-x-2 text-sm text-muted-foreground",
        className
      )}
      {...props}
    >
      <ol className="flex items-center space-x-2" role="list">
        {[{ name: 'Home', href: '/' }, ...breadcrumbItems].map((item, index) => {
          const isLast = index === breadcrumbItems.length; // +1 because we added Home
          const icon = finalIconMap[item.name];
          
          return (
            <li key={index} className="flex items-center space-x-2">
              {index > 0 && (
                <Icon 
                  name="ChevronRight" 
                  size={14} 
                  className="text-muted-foreground"
                  aria-hidden="true"
                />
              )}
              
              <div className="flex items-center space-x-1.5">
                {icon && (
                  <Icon 
                    name={icon} 
                    size={16} 
                    className={cn(
                      isLast 
                        ? "text-primary" 
                        : "text-muted-foreground"
                    )}
                  />
                )}
                
                {isLast ? (
                  <span 
                    className="font-medium text-foreground"
                    aria-current="page"
                  >
                    {item.name}
                  </span>
                ) : (
                  <Link
                    to={item.href}
                    className="hover:text-primary transition-colors duration-200 focus:outline-none focus:text-primary focus:underline"
                    aria-label={`Navigate to ${item.name}`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;